/**
 * GA4 Analytics — DISABLED UNTIL A REAL MEASUREMENT ID IS PROVIDED.
 *
 * Activation:
 *   1. Add the following line to /app/frontend/.env (never commit real IDs):
 *        REACT_APP_GA4_MEASUREMENT_ID=G-XXXXXXXXXX
 *   2. Redeploy the frontend.
 *
 * Guarantees:
 *   - No script tag is injected while REACT_APP_GA4_MEASUREMENT_ID is missing
 *     or does not match /^G-[A-Z0-9]+$/.
 *   - The gtag script loads exactly once per page-load lifetime.
 *   - Page-view events are dispatched on every React Router change.
 *   - Only a redacted CTA identifier is sent on tel: link and primary CTA
 *     clicks — no name, phone, email, message, patient, or medical data ever
 *     reaches Google.
 *
 * Do NOT invoke this module with hard-coded IDs. Do NOT bypass the id gate.
 */

const ID_RE = /^G-[A-Z0-9]+$/;

function getMeasurementId() {
  const raw = (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();
  if (!raw) return null;
  if (!ID_RE.test(raw)) return null;
  return raw;
}

let _loaded = false;

/**
 * Loads gtag.js once. No-op if the id is missing or malformed.
 * Safe to call on every route change.
 */
export function initAnalytics() {
  if (_loaded) return true;
  const id = getMeasurementId();
  if (!id) return false;
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  // 1. gtag stub.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());

  // 2. Privacy defaults: no ad personalization, IP anonymization on.
  window.gtag('config', id, {
    anonymize_ip: true,
    allow_ad_personalization_signals: false,
    // We fire page_view manually on router changes below.
    send_page_view: false,
  });

  // 3. Load the gtag script.
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.setAttribute('data-managed-by', 'ga4-integration');
  document.head.appendChild(script);
  _loaded = true;
  return true;
}

/**
 * Track a virtual page view. Called from a React Router listener.
 * `path` should be the pathname (no query) — never a full URL with query,
 * since query strings occasionally contain form re-submit context.
 */
export function trackPageView(path, title) {
  const id = getMeasurementId();
  if (!id || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: `${window.location.origin}${path}`,
  });
}

/**
 * Track a CTA click. Pass a fixed identifier ONLY — never form contents.
 *   trackCta('phone_click_header')
 *   trackCta('book_ride_hero')
 */
export function trackCta(cta_id, extras = {}) {
  const id = getMeasurementId();
  if (!id || typeof window === 'undefined' || !window.gtag) return;
  // Sanitize: only whitelist safe primitives, cap string length, no PII keys.
  const FORBIDDEN = /name|email|phone|message|patient|dob|address|ssn|dob|medical|note|instruction/i;
  const safe = {};
  for (const [k, v] of Object.entries(extras)) {
    if (FORBIDDEN.test(k)) continue;
    if (typeof v === 'string') safe[k] = v.slice(0, 64);
    else if (typeof v === 'number' || typeof v === 'boolean') safe[k] = v;
  }
  window.gtag('event', 'cta_click', {
    cta_id: String(cta_id).slice(0, 64),
    ...safe,
  });
}

/**
 * Conversion events — safe to call unconditionally; no-op when GA4 not enabled.
 * Fire ONLY from the success branch of each form submit, AFTER the API returns 200.
 * Never pass form field values; only safe aggregate metadata (service_type, source_page).
 */
function _fireConversion(event_name, extras = {}) {
  const id = getMeasurementId();
  if (!id || typeof window === 'undefined' || !window.gtag) return;
  const FORBIDDEN = /name|email|phone|message|patient|dob|address|ssn|medical|note|instruction/i;
  const safe = {};
  for (const [k, v] of Object.entries(extras)) {
    if (FORBIDDEN.test(k)) continue;
    if (typeof v === 'string') safe[k] = v.slice(0, 64);
    else if (typeof v === 'number' || typeof v === 'boolean') safe[k] = v;
  }
  window.gtag('event', event_name, {
    event_category: 'conversion',
    non_interaction: false,
    ...safe,
  });
}

export const trackBookingFormSubmit = (extras = {}) => _fireConversion('booking_form_submit', extras);
export const trackContactFormSubmit = (extras = {}) => _fireConversion('contact_form_submit', extras);
export const trackQuoteFormSubmit   = (extras = {}) => _fireConversion('quote_form_submit', extras);
// Explicit click-to-call conversion. The global click handler also fires `cta_click`
// with cta_id="phone_click"; this is the GA4-conversion-named counterpart.
export const trackClickToCall      = (extras = {}) => _fireConversion('click_to_call', extras);

/**
 * Convenience: attach a global click listener that captures tel:/mailto: link
 * clicks and reports a redacted cta_id. Call once from App.js on mount.
 */
export function attachGlobalClickTracking() {
  const id = getMeasurementId();
  if (!id || typeof document === 'undefined') return;
  document.addEventListener(
    'click',
    (e) => {
      const anchor = e.target && e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      if (href.startsWith('tel:')) {
        trackCta('phone_click', { location: anchor.getAttribute('data-testid') || 'unknown' });
        _fireConversion('click_to_call', { location: anchor.getAttribute('data-testid') || 'unknown' });
      } else if (href.startsWith('mailto:')) {
        trackCta('email_click', { location: anchor.getAttribute('data-testid') || 'unknown' });
      }
    },
    { capture: true, passive: true }
  );
}
